import React from 'react';

import {
    MoreThanOneInputError,
    NoKeyError,
    FormIsEmptyError,
} from '../errors/errors';

const NATIVE_INPUT_TYPES = ['input', 'select'];

export function _getAllInputs(children, reactInputComponent) {
    return childrenToArray(children)
        .filter((child) => {
            return childIsInput(child, reactInputComponent);
        });
}

export function assertChildHasKey(child) {
    if (!child || !child.key) {
        throw new NoKeyError(child);
    }
}

export function assertFormIsNotEmpty(formData) {
    const normalizedFormData = normalizeFormData(formData);
    if (Object.keys(normalizedFormData).length === 0) {
        throw new FormIsEmptyError();
    }
}

export function augmentChildEvent(myFn, fn, key) {
    return createAugmentedFunction(myFn, fn, key);
}

export function childIsInput(child, reactInputComponent) {
    let isReactInput = false;
    if (reactInputComponent) {
        isReactInput = childIsInstanceOf(child, reactInputComponent);
    }

    return isReactInput || isNativeInput(child);
}

export function childIsInstanceOf(child, reactClass) {
    if (!reactClass) {
        return false;
    }

    /* istanbul ignore next */
    if (typeof module !== 'undefined' && module.hot) {
        const {
            displayName: componentDisplayName,
            name: componentName,
        } = reactClass.prototype.constructor;
        const {
            displayName: instanceDisplayName,
            name: instanceName,
        } = child.type;

        const instance = instanceDisplayName || instanceName;
        const cls = componentDisplayName || componentName;

        return instance === cls;
    }

    /* istanbul ignore next */
    return child.type === reactClass;
}

export function childrenToArray(reactChildren) {
    let children = [];

    React.Children.forEach(reactChildren, (child) => children.push(child));

    return children;
}

export function createAugmentedFunction(myFn, fn = () => {}, key) {
    return (...args) => {
        myFn(...args, key);
        fn(...args);
    };
}

export function getInput(children, reactInputComponent) {
    const inputs = _getAllInputs(children, reactInputComponent);
    if (inputs.length > 1) {
        const name = reactInputComponent
            ? reactInputComponent.name
            : 'undefined';
        throw new MoreThanOneInputError(name);
    }

    return inputs.shift();
}

export function getValue(child) {
    if (child.type === 'select') {
        const children = childrenToArray(child.props.children);
        const selectedOption = children.find((option) => {
            return option.props.defaultValue;
        });

        if (selectedOption) {
            return selectedOption.props.value;
        }

        return;
    }

    return child.props.value;
}

export function isNativeInput(child) {
    return NATIVE_INPUT_TYPES.includes(child.type);
}

/**
 * @param {Object} formData - key/value map
 * @param {boolean} includeMissingKeys=false - whether or not to include form data with no
 * value (if true, will be set to the empty string
 * @return {Object} a modified copy of `formData`
 */
export function normalizeFormData(formData, includeMissingKeys = false) {
    return Object.entries(formData).reduce((obj, [key, value]) => {
        if ((!value || value.length === 0) && !includeMissingKeys) {
            return obj;
        }

        return {
            ...obj,
            [key]: value || '',
        };
    }, {});
}
